import React, { useState, useEffect } from 'react';
import { Car, User, Shield, Umbrella, Clock } from 'lucide-react';
import { AddressLookup } from './AddressLookup';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/AuthContext';
import { AuthModal } from './AuthModal';
import { lookupVehicle } from '../lib/dvla';
import { useQuoteForm } from '../hooks/useQuoteForm';
import { useQuoteFormSubmission } from '../hooks/useQuoteFormSubmission';
import { FormControls } from './quoteforms/FormControls';
import { PersonalInfoStep } from './quoteforms/PersonalInfoStep';
import { VehicleInfoStep } from './quoteforms/VehicleInfoStep';
import { CoverageInfoStep } from './quoteforms/CoverageInfoStep';
import { StepIndicator } from './quoteforms/StepIndicator';
import { AccidentsConvictionsStep } from './quoteforms/AccidentsConvictionsStep';
import { VehicleModifications } from './VehicleModifications';
import { DriversInfoStep } from './quoteforms/DriversInfoStep';

interface FormData {
  step: number;
  startDate: string;
  personalInfo: {
    name: string;
    title: string;
    firstName: string;
    surname: string;
    gender: string;
    email: string;
    countryCode: string;
    phone: string;
    dateOfBirth: string;
    address: string;
    region: string;
  };
  driverInfo: {
    maritalStatus: string;
    isHomeOwner: string;
    hasAccidents: string;
    accidents: {
      date: string;
      fault: string;
      type: string;
      subType?: string;
      policy: string;
      noClaimsAffected: string;
      bodilyInjury: string;
      value: string;
    }[];
    hasConvictions: string;
    convictions: {
      offenceDate: string;
      convictionDate: string;
      hasFine: string;
      points: string;
      banLength: string;
      code: string;
    }[];
    livedInUKSinceBirth: string;
    employmentStatus: string;
    licenseType: string;
    licenseHeld: {
      years: string;
      months: string;
    };
    otherVehicles: string;
  };
  vehicleInfo: {
    make: string;
    model: string;
    ownership: {
      isOwner: string;
      isKeeper: string;
      purchaseDate: string;
      notPurchased: boolean;
      parkingLocation: string;
    };
    transmission: string;
    drivePosition: string;
    seats: string;
    alarm: string;
    tracker: string;
    year: string;
    value: string;
    registration: string;
    mileage: string;
    isModified: string;
    modifications: { type_id: string, details: string }[];
  };
  coverageInfo: {
    noClaimsYears: string;
    coverageType: string;
    protectNoClaims: boolean;
    additionalCover: string[];
    paymentFrequency: string;
    hadInsuranceBefore: string;
  };
}

interface QuoteFormProps {
  onSubmitSuccess?: () => void;
}

function QuoteForm({ onSubmitSuccess }: QuoteFormProps) {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const { user } = useAuth();
  const [formDataSaved, setFormDataSaved] = useState(false);
  const [requiresAuth, setRequiresAuth] = useState(false);
  const [isLookingUpVehicle, setIsLookingUpVehicle] = useState(false);

  const {
    formData,
    setFormData,
    error,
    setError,
    showModifications,
    setShowModifications,
    handleInputChange,
    nextStep,
    prevStep,
    handleModificationsComplete
  } = useQuoteForm(user);

  const { handleSubmit, isSubmitting, error: submitError } = useQuoteFormSubmission(onSubmitSuccess);

  const calculateBiddingEnd = (startDate: string): string => {
    const now = new Date();
    const biddingEnd = new Date(now.getTime() + parseInt(formData.biddingDuration) * 60 * 60 * 1000);
    const isWeekend = now.getDay() === 5 || now.getDay() === 6;
    
    // If submitted on weekend, extend to Monday 5pm
    if (isWeekend) {
      const monday = new Date(now);
      monday.setDate(monday.getDate() + (8 - now.getDay())); // Move to next Monday
      monday.setHours(17, 0, 0, 0);
      return monday.toLocaleString('en-GB', {
        weekday: 'long',
        hour: 'numeric',
        minute: 'numeric',
        day: 'numeric',
        month: 'long'
      });
    }
    
    return biddingEnd.toLocaleString('en-GB', {
      weekday: 'long',
      hour: 'numeric',
      minute: 'numeric',
      day: 'numeric',
      month: 'long'
    });
  };

  const handleVehicleLookup = async (registration: string) => {
    if (registration.length < 2) return;
    
    setIsLookingUpVehicle(true);
    setError(null);
    
    try {
      const vehicleDetails = await lookupVehicle(registration);
      
      setFormData(prev => ({
        ...prev,
        vehicleInfo: {
          ...prev.vehicleInfo,
          registration: vehicleDetails.registrationNumber,
          make: vehicleDetails.make,
          model: '',
          year: vehicleDetails.yearOfManufacture.toString(),
          value: '',
        }
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not fetch vehicle details');
    } finally {
      setIsLookingUpVehicle(false);
    }
  };

  const handleSubmitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submit handler triggered');
    
    // Validate required fields before proceeding
    if (!formData.personalInfo.firstName || !formData.personalInfo.surname || !formData.personalInfo.email) {
      setError('Please fill in all required fields');
      console.log('Form validation failed - missing required fields');
      return;
    }

    try {
      // Save form data first
      console.log('Saving form data to localStorage');
      localStorage.setItem('pendingQuoteRequest', JSON.stringify({
        data: formData,
        timestamp: new Date().toISOString(),
        personalInfo: {
          firstName: formData.personalInfo.firstName,
          surname: formData.personalInfo.surname,
          email: formData.personalInfo.email,
          phone: formData.personalInfo.phone,
          countryCode: formData.personalInfo.countryCode
        }
      }));

      // If user is not authenticated, show auth modal
      if (!user) {
        console.log('User not authenticated, showing auth modal');
        setRequiresAuth(true);
        setFormDataSaved(true);

        const authModal = document.getElementById('auth-modal');
        if (authModal) {
          authModal.dispatchEvent(new CustomEvent('prefill-email', {
            detail: { 
              email: formData.personalInfo.email,
              firstName: formData.personalInfo.firstName,
              surname: formData.personalInfo.surname,
              phone: formData.personalInfo.phone,
              countryCode: formData.personalInfo.countryCode
            }
          }));
        }
        setShowAuthModal(true);
        return;
      }

      // If user is authenticated, submit the quote
      const result = await handleSubmit(formData, user);
      if (result.success) {
        console.log('Quote submitted successfully');
        alert('Quote request submitted successfully!');
        localStorage.removeItem('pendingQuoteRequest');
        onSubmitSuccess?.();
      } else {
        console.log('Quote submission failed:', result.error);
        setError(result.error || 'Failed to submit quote request');
      }
    } catch (err) {
      console.error('Error submitting quote request:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit quote request');
    }
  };

  const calculateAge = (dob: string): number => {
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  };

  const renderStep = () => {
    switch (formData.step) {
      case 1:
        return (
          <div className="space-y-4">
            <div className="flex items-center space-x-2 mb-6">
              <Clock className="w-6 h-6 text-indigo-600" />
              <h2 className="text-xl font-semibold">Insurance Start Date</h2>
              <span className="text-sm text-gray-500 ml-2">(Bidding will end in 24-48 hours)</span>
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                When would you like your insurance to start?
              </label>
              <div className="space-y-4">
                <div className="flex gap-4">
                  <input
                    type="date"
                    value={formData.startDate || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                    min={new Date(Date.now() + 86400000).toISOString().split('T')[0]}
                    max={new Date(Date.now() + 86400000 * 30).toISOString().split('T')[0]}
                    className="flex-1 p-2 border rounded"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    How long should insurers have to bid?
                  </label>
                  <div className="flex space-x-4">
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        value="24"
                        checked={formData.biddingDuration === '24'}
                        onChange={(e) => setFormData(prev => ({ ...prev, biddingDuration: e.target.value }))}
                        className="rounded-full h-4 w-4 text-indigo-600"
                      />
                      <span className="ml-2">24 hours</span>
                    </label>
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        value="48"
                        checked={formData.biddingDuration === '48'}
                        onChange={(e) => setFormData(prev => ({ ...prev, biddingDuration: e.target.value }))}
                        className="rounded-full h-4 w-4 text-indigo-600"
                      />
                      <span className="ml-2">48 hours</span>
                    </label>
                  </div>
                </div>
                <p className="text-sm text-gray-500">
                  {formData.startDate ? (
                    <>
                      Bidding will end: {calculateBiddingEnd(formData.startDate)} 
                      <br />
                      <span className="text-xs">
                        {new Date().getDay() === 5 || new Date().getDay() === 6 
                          ? '(Extended to Monday due to weekend)'
                          : '(Insurers will have time to review and bid on your request)'}
                      </span>
                    </>
                  ) : (
                    'Select when you would like your insurance to start'
                  )}
                </p>
              </div>
            </div>
          </div>
        );
      case 2:
        return (
          <div className="space-y-6">
            <VehicleInfoStep
              data={formData.vehicleInfo}
              onInputChange={(field, value) => handleInputChange('vehicleInfo', field, value)}
              isLookingUpVehicle={isLookingUpVehicle}
              handleVehicleLookup={handleVehicleLookup}
            />
            {formData.vehicleInfo.isModified === 'Yes' && (
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="text-sm font-medium text-blue-900 mb-2">Vehicle Modifications</h4>
                <p className="text-sm text-blue-700 mb-4">
                  You've indicated that your vehicle has modifications. These need to be declared for accurate insurance quotes.
                </p>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={showModifications}
                    onChange={(e) => setShowModifications(e.target.checked)}
                    className="rounded text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-blue-800">
                    I'm ready to provide details about my vehicle modifications
                  </span>
                </label>
              </div>
            )}
          </div>
        );
      case 3:
        return <DriversInfoStep
          data={formData.driverInfo}
          onInputChange={(field, value) => handleInputChange('driverInfo', field, value)}
        />;
      case 4:
        return <AccidentsConvictionsStep
          data={{
            hasAccidents: formData.driverInfo.hasAccidents,
            accidents: formData.driverInfo.accidents,
            hasConvictions: formData.driverInfo.hasConvictions,
            convictions: formData.driverInfo.convictions
          }}
          onInputChange={(field, value) => handleInputChange('driverInfo', field, value)}
        />;
      case 5:
        return <PersonalInfoStep
          data={formData.personalInfo}
          onInputChange={(field, value) => handleInputChange('personalInfo', field, value)}
          calculateAge={calculateAge}
          user={user}
        />;
      case 6:
        return <CoverageInfoStep
          data={formData.coverageInfo}
          onInputChange={(field, value) => handleInputChange('coverageInfo', field, value)}
        />;
      default:
        return null;
    }
  };

  const handleNextStep = () => {
    // Scroll to top of form
    const formElement = document.getElementById('quote-form');
    if (formElement && window.scrollY > formElement.offsetTop) {
      window.scrollTo({
        top: formElement.offsetTop - 20,
        behavior: 'smooth'
      });
    }

    // Save form data on each step
    localStorage.setItem('pendingQuoteRequest', JSON.stringify({
      data: formData,
      timestamp: new Date().toISOString(),
      lastCompletedStep: formData.step,
      personalInfo: formData.personalInfo
    }));

    if (formData.step === 2 && formData.vehicleInfo.isModified === 'Yes') {
      setShowModifications(true);
      return;
    }
    
    nextStep();
  };

  return (
    <div id="quote-form" className="max-w-2xl mx-auto bg-white p-4 md:p-6 rounded-lg shadow-lg scroll-mt-16">
      <StepIndicator 
        currentStep={formData.step} 
        totalSteps={formData.vehicleInfo.isModified === 'Yes' ? 7 : 6}
        requiresAuth={requiresAuth}
      />
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)}
        enforceAuth={requiresAuth}
      />
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-600 text-sm">
          {error}
        </div>
      )}
      <form className="space-y-4" onSubmit={handleSubmitForm}>
        {formData.step === 2 && formData.vehicleInfo.isModified === 'Yes' && showModifications ? (
          <VehicleModifications
            onSave={handleModificationsComplete}
            onBack={() => setShowModifications(false)}
          />
        ) : (
          renderStep()
        )}
        <FormControls
          currentStep={formData.step}
          totalSteps={formData.vehicleInfo.isModified === 'Yes' ? 7 : 6}
          onPrevious={prevStep}
          onNext={handleNextStep}
          isSubmitting={isSubmitting}
          isLastStep={formData.step === 6}
        />
      </form>
    </div>
  );
}

export { QuoteForm };